import { Text } from '@avanta/shared/components/Text';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type Patient } from '@avanta/shared/types/patients';
import { getStatusBadge } from '@avanta/shared/utils/appointments';
import { useTranslation } from 'react-i18next';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { useFetchAppointmentByPatient } from '../../useFetchAppointmentByPatient';
import { CardActions } from './components/CardActions';
import { PsychologistInfo } from './components/PsychologistInfo';
import { SessionDetailsSection } from './components/SessionDetailsSection';

export const AppointmentCard = ({ patient }: { patient?: Patient }): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { data: patientRecord } = useFetchActivePatientRecord(patient?.id ?? '');
  const { data: appointment } = useFetchAppointmentByPatient();

  if (!appointment) {
    return null;
  }

  return (
    <Card className="relative h-85 am-md:p-8 *:data-[slot=card-content]:h-full">
      <div className="absolute top-4 right-8">{getStatusBadge(appointment)}</div>
      <div className="flex flex-col gap-13 h-full justify-between">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            {!appointment.canceled && (
              <Text variant="heading-sm-medium" className="text-denim-500">
                {t('AppointmentCard.details.heading')}
              </Text>
            )}
            {patientRecord?.code && (
              <Text variant="text-sm-regular" className="text-gray-400" tag="span">
                {t('AppointmentCard.details.recordNumber.label')}
                <Text variant="text-sm-regular" className="ml-1 text-denim-900" tag="span">
                  {patientRecord?.code}
                </Text>
              </Text>
            )}
          </div>
          <div className="flex justify-between items-center">
            <SessionDetailsSection appointment={appointment} />
            <PsychologistInfo appointment={appointment} />
          </div>
        </div>
        {!appointment.canceled && <CardActions appointment={appointment} patient={patient} />}
      </div>
    </Card>
  );
};
